import { gql } from '@apollo/client';
// eslint-disable-next-line import/no-cycle
import type { Segment } from '@fixter/i18n';
import type { IQuote } from '$apollo/gql/basket';
import type { IVehicle } from '../vehicle/types';

export interface ICredit {
  readonly balance: number;
  readonly used: number;
  readonly lastUpdate: string;
  readonly active?: boolean;
  readonly referralCode?: string;
}

export interface ICustomer {
  readonly id: string;
  readonly status: string;
  readonly firstName?: string;
  readonly lastName?: string;
  readonly mobileNumber?: string;
  readonly email?: string;
  readonly credit?: ICredit;
  readonly tags?: string[];
  readonly marketingConsent?: boolean;
}

export interface ICustomerVehicles {
  readonly vehicleId: string;
  readonly vrm: string;
  readonly make: string;
  readonly model: string;
  readonly lastReadMiles: number;
  readonly lastReadMileageDate: Date;
  readonly lastEstimatedMiles: number;
  readonly lastEstimatedMileageDate: Date;
  readonly logo: string;
  readonly nextTaxDate: string;
  readonly nextMotDate: string;
  readonly dateRegistered: string;
}

export interface IQueryGetCustomerData {
  readonly getCustomer: ICustomer;
}

export interface QueryGetCustomerVariables {
  readonly customerId?: ICustomer['id'];
}

export interface IDeleteCustomerVehicle {
  readonly deleteCustomerVehicle: { readonly vehicleId: string };
}

export interface MutationCreateCustomerParams {
  readonly firstName?: string;
  readonly lastName?: string;
  readonly email?: string;
  readonly phone?: string;
  readonly password?: string;
  readonly bookingId?: string;
  readonly purchaseOrder?: string;
  readonly marketingConsent?: boolean;
  readonly segment?: Segment;
}

export interface MutationCreateCustomerVariables {
  readonly customerInput: MutationCreateCustomerParams;
}

export interface MutationUpdateCustomerParams {
  readonly firstName?: string;
  readonly lastName?: string;
  readonly phone?: string;
  readonly bookingId?: string;
  readonly purchaseOrder?: string;
  readonly segment?: Segment;
}

export interface MutationUpdateCustomerVariables {
  readonly updateCustomerInput: MutationUpdateCustomerParams;
}

export const FRAGMENT_CUSTOMER_RESULT = gql`
  fragment CustomerResult on Customer {
    id
    status
    firstName
    lastName
    mobileNumber
    email
    credit {
      balance
      used
    }
    tags
  }
`;

export const QUERY_GET_CUSTOMER_CREDITS = gql`
  query getCustomer($customerId: String!) {
    getCustomer(customerId: $customerId) {
      id
      status
      firstName
      lastName
      mobileNumber
      email
      credit {
        balance
        used
        referralCode
      }
    }
  }
`;

// TODO check if we need credit here
export const QUERY_GET_CUSTOMER = gql`
  query getCustomer($customerId: String!) {
    getCustomer(customerId: $customerId) {
      ...CustomerResult
    }
  }
  ${FRAGMENT_CUSTOMER_RESULT}
`;

export interface ICustomerBookings {
  readonly bookingId: number;
  readonly bookingUuid: string;
  readonly vehicleId: string;
  readonly collectionDateTimeLocal?: string;
  readonly masterStatusId: number;
  readonly proposedWorkOperations: IQuote;
  readonly collectionAddress: { postcode: string };
  readonly vehicle: IVehicle;
}

export interface IQueryGetCustomerVehicles {
  readonly getCustomerVehicles: ICustomerVehicles[];
}

export interface IQueryGetCustomerBookings {
  readonly getCustomerBookings: ICustomerBookings[];
}

export const QUERY_GET_CUSTOMER_VEHICLES = gql`
  query getCustomerVehicles($customerId: String!) {
    getCustomerVehicles(customerId: $customerId) {
      vrm
      vehicleId
      make
      model
      lastReadMiles
      lastReadMileageDate
      logo
      nextTaxDate
      nextMotDate
    }
  }
`;

export const MUTATION_CREATE_CUSTOMER = gql`
  mutation createCustomer($customerInput: CustomerInput!) {
    createCustomer(customerInput: $customerInput) {
      ...CustomerResult
    }
  }
  ${FRAGMENT_CUSTOMER_RESULT}
`;

export const MUTATION_UPDATE_CUSTOMER = gql`
  mutation updateCustomer($updateCustomerInput: UpdateCustomerInput!) {
    updateCustomer(updateCustomerInput: $updateCustomerInput) {
      ...CustomerResult
    }
  }
  ${FRAGMENT_CUSTOMER_RESULT}
`;

export const MUTATION_LOGOUT = gql`
  mutation logout {
    logout
  }
`;

export const MUTATION_DELETE_CUSTOMER_VEHICLE = gql`
  mutation deleteCustomerVehicle($vehicleId: String!) {
    deleteCustomerVehicle(vehicleId: $vehicleId) {
      vehicleId
    }
  }
`;

export const MUTATION_ADD_REMINDER = gql`
  mutation setReminderEmail($email: String!, $vehicleId: String!, $reminderEmail: Boolean!) {
    setReminderEmail(email: $email, vehicleId: $vehicleId, reminderEmail: $reminderEmail)
  }
`;

export interface MutationCreateCallBackRequestParams {
  readonly phoneNumber?: string;
  readonly firstName?: string;
  readonly vrm?: string;
  readonly postcode?: string;
  readonly message?: string;
}
export interface MutationCreateCallBackRequestVariables {
  readonly createCallBackRequestInput: MutationCreateCallBackRequestParams;
}

export const MUTATION_CREATE_CALL_BACK_REQUEST = gql`
  mutation createCallBackRequest($createCallBackRequestInput: CreateCallBackRequestInput!) {
    createCallBackRequest(createCallBackRequestInput: $createCallBackRequestInput) {
      phoneNumber
    }
  }
`;

export const QUERY_GET_CUSTOMER_BOOKINGS = gql`
  query getCustomerBookings($filter: BookingFilter!) {
    getCustomerBookings(filter: $filter) {
      bookingId
      bookingUuid
      vehicleId
      collectionDateTimeLocal
      masterStatusId
      collectionAddress {
        postcode
      }
      proposedWorkOperations {
        quoteId
        lineItems {
          title
          operationId
        }
      }
      vehicle {
        vehicleId
        logo
        make
        model
        litre
        vrm
      }
    }
  }
`;
